import React, { useMemo } from 'react'
import type { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import Card from '@/components/Card'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import Radio from '@/components/Radio'
import RangeSlider from '@/components/RangeSlider'
import Select from '@/components/Select'
import { useAppSelector } from '@/hooks/redux'
import {
  FORM_FIELDS,
  type FormData,
} from '@/pages/getRecipe/allInOne/utils/formTypes'
import { updateAllInOneFormValue } from '@/store/allInOne'
import { useGetMetadataQuery } from '@/store/apiService'
import { REGION, SLIDER_CLASS, sliderRange } from '@/utils/constants'
import { sortAlphabetically } from '@/utils/helper'

import {
  DENTIN_TYPE,
  INDICATION_FORM,
  MATERIAL_TEXT,
  getOptions,
  imageType,
} from '../utils/constants'

interface IndicationProps {
  control: Control<FormData>
  recipeTypeValue: string
  requiredInputValidation: any
}

interface Metadata {
  available_indications: string[]
  available_ceramic_brands: any[]
  available_naturaldie: any[]
  available_capbrands: any[]
  available_stainingbrands: any[]
  [key: string]: any
}

const Indication: React.FC<IndicationProps> = ({
  control,

  recipeTypeValue,
  requiredInputValidation,
}) => {
  const { t } = useTranslation()

  const dispatch = useDispatch()

  const canvasImage = useAppSelector((state) => state.app.canvasImage)
  const { data: metadata } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as { data: Metadata; error: any; isLoading: boolean }

  const options = useMemo(
    () => ({
      indication: getOptions(metadata, 'available_indications'),
      ceramicBrands: getOptions(metadata, 'available_ceramic_brands'),
      aestheticModal: getOptions(metadata, 'available_naturaldie'),
      substructureBrand: sortAlphabetically(
        getOptions(metadata, 'available_capbrands') || [],
        'text',
      ),
      stainingBrands: sortAlphabetically(
        getOptions(metadata, 'available_stainingbrands') || [],
        'text',
      ),
    }),
    [metadata],
  )
  const indicationValue = useAppSelector(
    (state) => state.allInOne.form[FORM_FIELDS.INDICATION],
  )
  const brandOptions = useMemo(() => {
    const sortedOptions = sortAlphabetically(
      options?.ceramicBrands || [],
      'text',
    )
    return sortedOptions.map((option: any) => ({
      ...option,
      isDisabled:
        !metadata.compatibility_brand_indication?.[option.value]?.includes(
          indicationValue,
        ) ||
        (canvasImage?.type === imageType.OPTISHADE &&
          !option.optishade_supported) ||
        (canvasImage?.type === imageType.RAW && !option.camera_supported),
    }))
  }, [options.ceramicBrands, indicationValue, canvasImage, metadata])
  return (
    <>
      <Card>
        <Radio
          name={FORM_FIELDS.RECIPE_TYPE}
          control={control}
          label={t(INDICATION_FORM.recipeType.label)}
          options={INDICATION_FORM.recipeType.options.map((opt) => ({
            ...opt,
            label: t(opt.label),
          }))}
          tooltip={t(INDICATION_FORM.recipeType.tooltip)}
          onFormValueChange={(name, value) =>
            dispatch(
              updateAllInOneFormValue({ name: FORM_FIELDS.RECIPE_TYPE, value }),
            )
          }
          formStateKey="allInOne"
        />
      </Card>
      <Card>
        <Select
          options={options.indication}
          title={t(INDICATION_FORM.indication.title)}
          tooltipText={t(INDICATION_FORM.indication.tooltip)}
          control={control}
          name={FORM_FIELDS.INDICATION}
          onFormValueChange={(name, value) =>
            dispatch(
              updateAllInOneFormValue({ name: FORM_FIELDS.INDICATION, value }),
            )
          }
        />
      </Card>
      <Card>
        <Select
          options={brandOptions}
          title={t(MATERIAL_TEXT.ceramicBrands.title)}
          tooltipText={t(MATERIAL_TEXT.ceramicBrands.tooltip)}
          control={control}
          name={FORM_FIELDS.BRAND}
          onFormValueChange={(name, value) =>
            dispatch(
              updateAllInOneFormValue({ name: FORM_FIELDS.BRAND, value }),
            )
          }
          optionDisabledChecker={(option) => option.isDisabled}
        />
      </Card>
      {requiredInputValidation[FORM_FIELDS.NATURAL_DIE_MATERIAL] && (
        <Card>
          <Select
            options={options.aestheticModal}
            title={t(MATERIAL_TEXT.aestheticModel.title)}
            tooltipText={t(MATERIAL_TEXT.aestheticModel.tooltip)}
            control={control}
            name={FORM_FIELDS.NATURAL_DIE_MATERIAL}
            onFormValueChange={(name, value) =>
              dispatch(
                updateAllInOneFormValue({
                  name: FORM_FIELDS.NATURAL_DIE_MATERIAL,
                  value,
                }),
              )
            }
          />
        </Card>
      )}
      {requiredInputValidation[FORM_FIELDS.CAPBRAND] && (
        <Card>
          <Select
            options={options.substructureBrand}
            title={t(MATERIAL_TEXT.framework.title)}
            tooltipText={t(MATERIAL_TEXT.framework.tooltip)}
            control={control}
            name={FORM_FIELDS.CAPBRAND}
            onFormValueChange={(name, value) =>
              dispatch(
                updateAllInOneFormValue({ name: FORM_FIELDS.CAPBRAND, value }),
              )
            }
            optionDisabledChecker={(item) => {
              return (
                !requiredInputValidation.capbrand ||
                (canvasImage?.type === imageType.OPTISHADE &&
                  !item.optishade_supported) ||
                (canvasImage?.type === imageType.RAW && !item.camera_supported)
              )
            }}
          />
        </Card>
      )}
      <Card>
        <Select
          options={options.stainingBrands}
          title={t(MATERIAL_TEXT.frameworkStaining.title)}
          tooltipText={t(MATERIAL_TEXT.frameworkStaining.tooltip)}
          control={control}
          name={FORM_FIELDS.STAININGBRAND}
          onFormValueChange={(name, value) =>
            dispatch(
              updateAllInOneFormValue({
                name: FORM_FIELDS.STAININGBRAND,
                value,
              }),
            )
          }
        />
      </Card>
      <Card>
        <LabelWithTooltip
          label={t(MATERIAL_TEXT.availableSpace.title)}
          tooltipText={t(MATERIAL_TEXT.availableSpace.tooltip)}
        />
        {recipeTypeValue === DENTIN_TYPE.TRIPLE_DENTIN && (
          <RangeSlider
            name={FORM_FIELDS.AVAILSPACE_CERVICAL}
            label={t(REGION.CERVICAL)}
            control={control}
            sliderRange={sliderRange}
            step={0.1}
            className={SLIDER_CLASS.CERVICAL}
            onFormValueChange={(name, value) =>
              dispatch(
                updateAllInOneFormValue({
                  name: FORM_FIELDS.AVAILSPACE_CERVICAL,
                  value,
                }),
              )
            }
          />
        )}
        <RangeSlider
          name={FORM_FIELDS.AVAILSPACE_MIDDLE}
          label={t(REGION.MIDDLE)}
          control={control}
          sliderRange={sliderRange}
          step={0.1}
          className={SLIDER_CLASS.MIDDLE}
          onFormValueChange={(name, value) =>
            dispatch(
              updateAllInOneFormValue({
                name: FORM_FIELDS.AVAILSPACE_MIDDLE,
                value,
              }),
            )
          }
        />
        {recipeTypeValue === DENTIN_TYPE.TRIPLE_DENTIN && (
          <RangeSlider
            name={FORM_FIELDS.AVAILSPACE_INCISAL}
            label={t(REGION.INCISAL)}
            control={control}
            sliderRange={sliderRange}
            step={0.1}
            className={SLIDER_CLASS.INCISAL}
            onFormValueChange={(name, value) =>
              dispatch(
                updateAllInOneFormValue({
                  name: FORM_FIELDS.AVAILSPACE_INCISAL,
                  value,
                }),
              )
            }
          />
        )}
      </Card>
    </>
  )
}

export default Indication
